import { useContext, useLayoutEffect, useRef } from 'react';
import { Matrix4 } from 'three';

import { SDFTextProviderContext } from './context';
import { ISDFTextConfig, ISDFTextInstance, ISDFTextStyle } from './types';
import { makeStyleId } from './utils';

type Props = {
  instanceKey: string;
  matrixWorld: Matrix4;
} & ISDFTextConfig &
  ISDFTextStyle;

export function SDFText({
  instanceKey,
  matrixWorld,
  bold,
  color,
  opacity,
  outlineWidth,
  outlineOpacity,
  outlineColor,
  text,
  alignX,
  alignY,
  letterSpacing,
  yShift,
  width,
  lineHeight,
}: Props) {
  const services = useContext(SDFTextProviderContext);
  if (!services) throw new Error('No SDFTextProvider');

  const instanceRef = useRef<ISDFTextInstance | null>(null);
  const appliedMatrixWorldRef = useRef<Matrix4 | null>(null);

  useLayoutEffect(() => {
    const textStyle = { bold, color, opacity, outlineWidth, outlineOpacity, outlineColor };
    const textConfig = { text, alignX, alignY, letterSpacing, yShift, width, lineHeight };
    const styleId = makeStyleId(textStyle);
    instanceRef.current = services.insertInstance(styleId, textStyle, instanceKey, textConfig);
    appliedMatrixWorldRef.current = null;
    return () => services.removeInstance(styleId, instanceKey);
  }, [
    services,
    instanceKey,
    text,
    alignX,
    alignY,
    letterSpacing,
    yShift,
    width,
    lineHeight,
    bold,
    color,
    opacity,
    outlineWidth,
    outlineOpacity,
    outlineColor,
  ]);

  useLayoutEffect(() => {
    if (appliedMatrixWorldRef.current === matrixWorld) return;

    instanceRef.current?.setTransform(matrixWorld);
    appliedMatrixWorldRef.current = matrixWorld;
  });

  return null;
}
